import { ChangeEvent, useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import DialogActions from "components/Actions/dialogs/components/DialogActions";
import { EditInlineDialogProps } from "components/Actions/dialogs/types";
import Autocomplete from "components/DesignComponents/Autocomplete";
import Checkbox from "components/DesignComponents/Checkbox";
import Dialog from "components/DesignComponents/Dialog";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import ParamsContext from "context/params/paramsContext";
import { useApi } from "shared/hooks/useApi";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import {
    DROPDOWN_SIZES,
    ENDPOINT_PATH_SEGMENTS,
    ENDPOINTS,
    FILTER_NAMES,
    PLATFORMS,
    TABLE_NAMES
} from "shared/types/enums";

import { Value } from "./types";

const CloneModelInline = ({ data, isOpen, close }: EditInlineDialogProps) => {
    const { setReloadItems } = useContext(ParamsContext);

    const { t } = useTranslation();
    const { toMd } = useMediaQueries();
    const { postData, handleResponse } = useApi();

    const [isLoading, setIsLoading] = useState(false);

    const [value, setValue] = useState<Value>({
        name: "",
        id: null,
        platform: null,
        web_editor_support: null
    });

    const { name, id, platform, web_editor_support } = value;

    useEffect(() => {
        if (data) {
            const { id, platform, web_editor_support } = data;

            setValue({
                ...value,
                [FILTER_NAMES.Id]: id,
                [FILTER_NAMES.Platform]: platform,
                [FILTER_NAMES.WebEditorSupport]: web_editor_support
            });
        }
        // eslint-disable-next-line
    }, [data]);

    const platforms = Object.values(PLATFORMS).filter(
        platform => platform !== PLATFORMS.Unknown
    );

    const changeName = (e: ChangeEvent<HTMLInputElement>) =>
        setValue(prev => ({ ...prev, name: e.target.value }));

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            const response = await postData(
                `${ENDPOINTS.DeviceModels}/${id}${ENDPOINT_PATH_SEGMENTS.Clone}`,
                { name }
            );

            close();
            setReloadItems(TABLE_NAMES.DeviceModels);
            handleResponse(response);
        } catch (error) {
            handleResponse(error);
        }

        setIsLoading(false);
    };

    return (
        <Dialog
            title={t("Dialog##create model")}
            description={t("Dialog##create model description")}
            isTitleSeparator
            isActionsSeparator
            open={isOpen}
            close={close}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        setValue({
                            name: "",
                            id: null,
                            platform: null,
                            web_editor_support: null
                        });

                        setIsLoading(false);
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={close}
                    isLoading={isLoading}
                    isReadyToConfirm={
                        name &&
                        id !== null &&
                        platform &&
                        web_editor_support !== null
                    }
                />
            }
            extendToMaxWidth={toMd}
        >
            <InputField
                autoFocus
                size={INPUT_FIELD_SIZES.Medium}
                fullWidth
                required
                readOnly={isLoading}
                onChange={changeName}
                labelLeft={t("Dialog##name")}
                placeholder={t("Dialog##enter name")}
                customStyle={{ marginBottom: "16px" }}
            />

            <Autocomplete
                fullWidth
                labelLeft={t("Table##platform")}
                size={DROPDOWN_SIZES.Medium}
                options={platforms}
                value={platform}
                readOnly
            />

            <Checkbox
                label={t("Table##tct web support")}
                customStyle={{ marginTop: "16px" }}
                checked={web_editor_support || false}
                disabled
            />
        </Dialog>
    );
};

export default CloneModelInline;
